import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Select from "react-select";
import Tooltip from "@material-ui/core/Tooltip";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Row,
  FormGroup,
  Label,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { getcycleRenewallist } from "store/cycle/actions";

const SCHEME_GROUPS = {
  General_Chemistry: [29, 45],
  Immunoassay: [33, 46],
};

const getSchemeGroup = schemeId => {
  return (
    Object.entries(SCHEME_GROUPS).find(([_, ids]) =>
      ids.includes(schemeId)
    )?.[0] || schemeId
  );
};

class RenewalParticipants extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedScheme: "",
      modalOpen: false,
      modalSchemes: [],
      statusFilter: "",
      provinceFilter: "",
      districtFilter: "",
      nameFilter: "",
      cityFilter: "",
      sortAsc: true,
    };
  }

  componentDidMount() {
    const { match, onGetCycleRenewalList, location } = this.props;
    const id = match.params.id;
    if (id) onGetCycleRenewalList(id);

    const params = new URLSearchParams(location.search);
    this.setState({
      nameFilter: params.get("name") || "",
      cityFilter: params.get("city") || "",
      provinceFilter: params.get("province") || "",
      districtFilter: params.get("district") || "",
      statusFilter: params.get("status") || "",
      selectedScheme: params.get("scheme") || "",
    });
  }

  updateQueryParam = (key, value) => {
    const params = new URLSearchParams(this.props.location.search);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    this.props.history.replace({ search: params.toString() });
  };

  toggleModal = () => {
    this.setState({ modalOpen: !this.state.modalOpen });
  };

  openSchemeModal = schemeList => {
    this.setState({ modalSchemes: schemeList, modalOpen: true });
  };

  handleSchemeChange = e => {
    const value = e.target.value;
    this.setState({ selectedScheme: value });
    this.updateQueryParam("scheme", value);
  };

  handleProvinceChange = e => {
    const value = e.target.value;
    this.setState({ provinceFilter: value, districtFilter: "" });
    this.updateQueryParam("province", value);
    this.updateQueryParam("district", "");
  };

  handleDistrictChange = e => {
    const value = e.target.value;
    this.setState({ districtFilter: value });
    this.updateQueryParam("district", value);
  };

  handleCityChange = e => {
    const value = e.target.value;
    this.setState({ cityFilter: value });
    this.updateQueryParam("city", value);
  };

  formatDate = dateStr => {
    if (!dateStr || dateStr === "Not Paid" || dateStr === "-") return dateStr;
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" }).substring(0, 3);
    const year = String(d.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  formatAmount = amt => {
    if (!amt || amt === "-" || amt === "Not Paid") return amt;
    return parseFloat(amt).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  getRenewalStatus = (prevPayments = [], nextPayments = []) => {
    const prevGroups = new Set(
      prevPayments.flatMap(p =>
        (p.schemes || []).map(s => getSchemeGroup(s.scheme_id))
      )
    );
    const nextGroups = new Set(
      nextPayments.flatMap(p =>
        (p.schemes || []).map(s => getSchemeGroup(s.scheme_id))
      )
    );

    if (prevGroups.size === 0 && nextGroups.size > 0) return "New";
    if (prevGroups.size > 0 && nextGroups.size === 0) return "Pending";

    const allRenewed = [...prevGroups].every(g => nextGroups.has(g));
    return allRenewed ? "Renewed" : "RIP";
  };

  render() {
    const { CycleRenewalList } = this.props;
    const {
      selectedScheme,
      modalOpen,
      modalSchemes,
      statusFilter,
      provinceFilter,
      districtFilter,
      cityFilter,
      nameFilter,
    } = this.state;

    // ── STEP 1: split raw data ──────────────────────────────────────────────
    const oldParts = CycleRenewalList.filter(p =>
      p.payments?.some(pay => pay.cycle_reportstatus === "Old")
    );
    const prevParts = CycleRenewalList.filter(p =>
      p.payments?.some(pay => pay.cycle_reportstatus === "Previous")
    );
    const nextParts = CycleRenewalList.filter(p =>
      p.payments?.some(pay => pay.cycle_reportstatus === "New")
    );

    const participantIds = Array.from(
      new Set([
        ...oldParts.map(p => p.participant_id),
        ...prevParts.map(p => p.participant_id),
        ...nextParts.map(p => p.participant_id),
      ])
    );

    const allParticipants = Array.from(
      new Set([...prevParts, ...nextParts].map(p => p.name).filter(Boolean))
    );

    // ── STEP 2: build rows (with filters applied) ───────────────────────────
    const rows = participantIds
      .map(pid => {
        const oldP = oldParts.find(p => p.participant_id === pid) || {};
        const prevP = prevParts.find(p => p.participant_id === pid) || {};
        const nextP = nextParts.find(p => p.participant_id === pid) || {};
        const name = oldP.name || prevP.name || nextP.name || "";
        const city = oldP.city || prevP.city || nextP.city || "";
        const province =
          oldP.province || prevP.province || nextP.province || "";
        const district =
          oldP.district || prevP.district || nextP.district || "";

        if (provinceFilter && provinceFilter !== province) return null;
        if (districtFilter && districtFilter !== district) return null;
        if (
          cityFilter &&
          !city.toLowerCase().includes(cityFilter.toLowerCase())
        )
          return null;
        if (
          nameFilter &&
          !name.toLowerCase().includes(nameFilter.toLowerCase())
        )
          return null;

        if (
          selectedScheme &&
          ![prevP, nextP].some(p =>
            (p.payments || []).some(pay =>
              (pay.scheme_details || []).some(
                s => s.scheme_name === selectedScheme
              )
            )
          )
        ) {
          return null;
        }
        const oldPayments = (oldP.payments || [])
          .filter(p => p.cycle_reportstatus === "Old")
          .map(p => {
            const schemes = (p.scheme_details || []).filter(
              s => !selectedScheme || s.scheme_name === selectedScheme
            );
            return {
              price: parseFloat(p.amount) || 0,
              part_payment_amount: parseFloat(p.part_payment_amount) || 0,
              payment_settlement: p.payment_settlement || "Full",
              date: p.paydate,
              schemes,
              schemeCount: schemes.length,
              cycle_no: p.cycle_no,
            };
          })
          .filter(p => p.schemeCount > 0);
        const previousPayments = (prevP.payments || [])
          .filter(p => p.cycle_reportstatus === "Previous")
          .map(p => {
            const schemes = (p.scheme_details || []).filter(
              s => !selectedScheme || s.scheme_name === selectedScheme
            );
            return {
              price: parseFloat(p.amount) || 0,
              part_payment_amount: parseFloat(p.part_payment_amount) || 0, // ✅ add
              payment_settlement: p.payment_settlement || "Full",
              date: p.paydate,
              schemes,
              schemeCount: schemes.length,
              cycle_no: p.cycle_no,
            };
          })
          .filter(p => p.schemeCount > 0);

        const nextPayments = (nextP.payments || [])
          .filter(p => p.cycle_reportstatus === "New")
          .map(p => {
            const schemes = (p.scheme_details || []).filter(
              s => !selectedScheme || s.scheme_name === selectedScheme
            );
            return {
              price: parseFloat(p.amount) || 0,
              part_payment_amount: parseFloat(p.part_payment_amount) || 0, // ✅ add
              payment_settlement: p.payment_settlement || "Full",
              date: p.paydate,
              schemes,
              schemeCount: schemes.length,
              cycle_no: p.cycle_no,
            };
          })
          .filter(p => p.schemeCount > 0);

        return {
          participant_id: pid,
          city,
          province,
          district,
          name,
          old: oldPayments,
          previous: previousPayments,
          next: nextPayments,
        };
      })
      .filter(Boolean);

    // ── STEP 3: filteredRows (status filter on top of rows) ─────────────────
    const filteredRows = statusFilter
      ? rows.filter(
          r => this.getRenewalStatus(r.previous, r.next) === statusFilter
        )
      : rows;
    // ADD this near totalPrevAmount:
    const totalOldAmount = filteredRows.reduce(
      (sum, r) =>
        sum +
        r.old.reduce(
          (s, p) =>
            s +
            (p.payment_settlement === "Full"
              ? p.price || 0
              : p.part_payment_amount || 0),
          0
        ),
      0
    );

    const oldParticipantCount = filteredRows.filter(
      r => r.old && r.old.length > 0
    ).length;
    // ── STEP 4: totals (depend on filteredRows) ─────────────────────────────
    const totalPrevAmount = filteredRows.reduce(
      (sum, r) =>
        sum +
        r.previous.reduce(
          (s, p) =>
            s +
            (p.payment_settlement === "Full"
              ? p.price || 0
              : p.part_payment_amount || 0),
          0
        ),
      0
    );

    const totalNextAmount = filteredRows.reduce(
      (sum, r) =>
        sum +
        r.next.reduce(
          (s, p) =>
            s +
            (p.payment_settlement === "Full"
              ? p.price || 0
              : p.part_payment_amount || 0),
          0
        ),
      0
    );

    // ── STEP 5: sorted display rows ─────────────────────────────────────────
    const displayedRows = [...filteredRows].sort((a, b) => {
      const valA = a.previous?.length || 0;
      const valB = b.previous?.length || 0;
      return this.state.sortAsc ? valA - valB : valB - valA;
    });

    const prevParticipantCount = filteredRows.filter(
      r => r.previous && r.previous.length > 0
    ).length;

    const nextParticipantCount = filteredRows.filter(
      r => r.next && r.next.length > 0
    ).length;

    // ── STEP 6: dropdown options ────────────────────────────────────────────
    const provinces = Array.from(
      new Set([...prevParts, ...nextParts].map(p => p.province).filter(Boolean))
    );
    const districts = Array.from(
      new Set(
        [...prevParts, ...nextParts]
          .filter(p => !provinceFilter || p.province === provinceFilter)
          .map(p => p.district)
          .filter(Boolean)
      )
    );
    const cities = Array.from(
      new Set(
        [...prevParts, ...nextParts]
          .filter(
            p =>
              (!provinceFilter || p.province === provinceFilter) &&
              (!districtFilter || p.district === districtFilter)
          )
          .map(p => p.city)
          .filter(Boolean)
      )
    );

    return (
      <div
        className="container-fluid"
        style={{ paddingTop: "100px", paddingBottom: "50px" }}
      >
        <Row className="mt-5 mb-3 ps-5" style={{ top: "70px", zIndex: 1000 }}>
          {/* Note card */}
          <Row className="mb-3 ps-2">
            <Col>
              <Card className="shadow-sm p-2">
                <div>
                  <strong>Note:</strong>
                  <ul style={{ marginBottom: 0, paddingLeft: "20px" }}>
                    <li>
                      <strong>New:</strong> Participant has new schemes without
                      previous ones.
                    </li>
                    <li>
                      <strong>Pending:</strong> Participant has previous schemes
                      but no new schemes yet.
                    </li>
                    <li>
                      <strong>Renewed:</strong> All previous schemes are renewed
                      in the next cycle.
                    </li>
                    <li>
                      <strong>RIP:</strong> Some schemes are renewed but not
                      all.
                    </li>
                  </ul>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Filter: Participant */}
          <Col md={2}>
            <FormGroup>
              <Label>Filter by Participant</Label>
              <Select
                options={allParticipants.map(name => ({
                  label: name,
                  value: name,
                }))}
                value={
                  nameFilter ? { label: nameFilter, value: nameFilter } : null
                }
                onChange={selected => {
                  const value = selected ? selected.value : "";
                  this.setState({ nameFilter: value });
                  this.updateQueryParam("name", value);
                }}
                isClearable
                placeholder="Type or select participant"
              />
            </FormGroup>
          </Col>

          {/* Filter: Scheme */}
          <Col md={2}>
            <FormGroup>
              <Label>Filter by Scheme</Label>
              <Input
                type="select"
                value={selectedScheme}
                onChange={this.handleSchemeChange}
              >
                <option value="">All Schemes</option>
                {Array.from(
                  new Set(
                    [...prevParts, ...nextParts].flatMap(p =>
                      (p.payments || []).flatMap(pay =>
                        (pay.scheme_details || []).map(s => s.scheme_name)
                      )
                    )
                  )
                ).map((s, i) => (
                  <option key={i} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          {/* Filter: Status */}
          <Col md={2}>
            <FormGroup>
              <Label>Filter by Status</Label>
              <Input
                type="select"
                value={statusFilter}
                onChange={e => {
                  const value = e.target.value;
                  this.setState({ statusFilter: value });
                  this.updateQueryParam("status", value);
                }}
              >
                <option value="">All Status</option>
                <option value="New">New</option>
                <option value="Renewed">Renewed</option>
                <option value="Pending">Pending</option>
                <option value="RIP">RIP</option>
              </Input>
            </FormGroup>
          </Col>

          {/* Filter: Province */}
          <Col md={2}>
            <FormGroup>
              <Label>Filter by Province</Label>
              <Input
                type="select"
                value={provinceFilter}
                onChange={this.handleProvinceChange}
              >
                <option value="">All Provinces</option>
                {provinces.map((prov, i) => (
                  <option key={i} value={prov}>
                    {prov}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          {/* Filter: District */}
          <Col md={2}>
            <FormGroup>
              <Label>Filter by District</Label>
              <Input
                type="select"
                value={districtFilter}
                onChange={this.handleDistrictChange}
              >
                <option value="">All Districts</option>
                {districts.map((dist, i) => (
                  <option key={i} value={dist}>
                    {dist}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          {/* Filter: City */}
          <Col md={2}>
            <FormGroup>
              <Label>Filter by City</Label>
              <Input
                type="select"
                value={this.state.cityFilter}
                onChange={this.handleCityChange}
              >
                <option value="">All Cities</option>
                {cities.map((city, i) => (
                  <option key={i} value={city}>
                    {city}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card className="shadow-sm">
              <CardBody>
                <Table bordered responsive>
                  <thead className="table-light">
                    <tr>
                      <th style={{ backgroundColor: "#f9e8f8" }}>
                        City
                        <Input
                          type="text"
                          placeholder="Filter by City"
                          value={cityFilter}
                          onChange={e => {
                            const value = e.target.value;
                            this.setState({ cityFilter: value });
                            this.updateQueryParam("city", value);
                          }}
                          bsSize="sm"
                          style={{ marginTop: "5px" }}
                        />
                      </th>

                      <th style={{ backgroundColor: "#f9e8f8" }}>
                        Participant Name
                        <Input
                          type="text"
                          placeholder="Filter by Name"
                          value={nameFilter}
                          onChange={e => {
                            const value = e.target.value;
                            this.setState({ nameFilter: value });
                            this.updateQueryParam("name", value);
                          }}
                          bsSize="sm"
                          style={{ marginTop: "5px" }}
                        />
                      </th>
                      <th
                        colSpan="3"
                        className="text-center"
                        style={{ backgroundColor: "#d4edda" }}
                      >
                        Last Participants ({oldParticipantCount})
                        <br />
                        <h6 style={{ color: "#000000" }}>
                          Total: {this.formatAmount(totalOldAmount)}
                        </h6>
                      </th>
                      <th
                        colSpan="3"
                        className="text-center"
                        style={{ backgroundColor: "#d0e7ff" }}
                      >
                        Current Participants ({prevParticipantCount})
                        <br />
                        <h6 style={{ color: "#000000" }}>
                          Total: {this.formatAmount(totalPrevAmount)}
                        </h6>
                      </th>

                      <th
                        colSpan="3"
                        className="text-center"
                        style={{ backgroundColor: "#f3de88ff" }}
                      >
                        Next Participants ({nextParticipantCount})
                        <br />
                        <h6 style={{ color: "#000000" }}>
                          Total: {this.formatAmount(totalNextAmount)}
                        </h6>
                      </th>

                      <th rowSpan="2" style={{ backgroundColor: "#b4d8adff" }}>
                        Renewal Status
                      </th>
                      <th
                        rowSpan="2"
                        style={{
                          backgroundColor: "#e3e3e3",
                          textAlign: "center",
                        }}
                      >
                        Action
                      </th>
                    </tr>

                    <tr>
                      <th style={{ backgroundColor: "#f9e8f8" }}></th>
                      <th style={{ backgroundColor: "#f9e8f8" }}></th>
                      <th
                        style={{
                          backgroundColor: "#d4edda",
                          cursor: "pointer",
                        }}
                      >
                        Scheme
                      </th>
                      <th style={{ backgroundColor: "#d4edda" }}>
                        Paid Amount
                      </th>
                      <th style={{ backgroundColor: "#d4edda" }}>
                        Payment Date
                      </th>
                      <th
                        style={{
                          backgroundColor: "#d0e7ff",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          this.setState({ sortAsc: !this.state.sortAsc })
                        }
                      >
                        Scheme
                      </th>
                      <th style={{ backgroundColor: "#d0e7ff" }}>
                        Paid Amount
                      </th>
                      <th style={{ backgroundColor: "#d0e7ff" }}>
                        Payment Date
                      </th>

                      <th
                        style={{
                          backgroundColor: "#f3de88ff",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          this.setState({ sortAsc: !this.state.sortAsc })
                        }
                      >
                        Scheme
                      </th>
                      <th
                        style={{
                          backgroundColor: "#f3de88ff",
                          textAlign: "right",
                        }}
                      >
                        Paid Amount
                      </th>
                      <th style={{ backgroundColor: "#f3de88ff" }}>
                        Payment Date
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center">
                          No participants found
                        </td>
                      </tr>
                    ) : (
                      displayedRows.map((r, idx) => {
                        const prevRows = r.previous || [];
                        const nextRows = r.next || [];

                        const prevCount = prevRows.reduce(
                          (sum, p) => sum + (p.schemeCount || 0),
                          0
                        );
                        const nextCount = nextRows.reduce(
                          (sum, p) => sum + (p.schemeCount || 0),
                          0
                        );

                        const prevAmounts = prevRows.map((p, i) => (
                          <React.Fragment key={i}>
                            {p.payment_settlement === "Full"
                              ? this.formatAmount(p.price || 0)
                              : this.formatAmount(p.part_payment_amount || 0)}
                            {i !== prevRows.length - 1 && <br />}
                          </React.Fragment>
                        ));

                        const nextAmounts = nextRows.map((p, i) => (
                          <React.Fragment key={i}>
                            {p.payment_settlement === "Full"
                              ? this.formatAmount(p.price || 0)
                              : this.formatAmount(p.part_payment_amount || 0)}
                            {i !== nextRows.length - 1 && <br />}
                          </React.Fragment>
                        ));

                        const prevDate =
                          prevRows.length > 0
                            ? prevRows.map((p, i) => (
                                <React.Fragment key={i}>
                                  {this.formatDate(p.date)}
                                  {i !== prevRows.length - 1 && <br />}
                                </React.Fragment>
                              ))
                            : "-";

                        const nextDate =
                          nextRows.length > 0
                            ? nextRows.map((p, i) => (
                                <React.Fragment key={i}>
                                  {this.formatDate(p.date)}
                                  {i !== nextRows.length - 1 && <br />}
                                </React.Fragment>
                              ))
                            : "-";

                        const status = this.getRenewalStatus(
                          r.previous,
                          r.next
                        );
                        if (statusFilter && statusFilter !== status)
                          return null;

                        return (
                          <tr key={idx}>
                            <td
                              valign="middle"
                              style={{
                                background: "#f9e8f8",
                                textAlign: "left",
                              }}
                            >
                              {r.city}
                            </td>
                            <td
                              valign="middle"
                              style={{
                                background: "#f9e8f8",
                                textAlign: "left",
                              }}
                            >
                              {r.name}
                            </td>
                            <td
                              valign="middle"
                              style={{
                                background: "#d4edda",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                this.openSchemeModal(
                                  (r.old || []).flatMap(p =>
                                    (p.schemes || []).map(s => ({
                                      scheme_name: s.scheme_name,
                                      cycle_no: s.cycle_no || "-",
                                    }))
                                  )
                                )
                              }
                            >
                              {(r.old || []).reduce(
                                (sum, p) => sum + (p.schemeCount || 0),
                                0
                              ) > 0
                                ? (r.old || []).reduce(
                                    (sum, p) => sum + (p.schemeCount || 0),
                                    0
                                  )
                                : "-"}
                            </td>
                            <td
                              valign="middle"
                              style={{ background: "#d4edda" }}
                              align="right"
                            >
                              {(r.old || []).map((p, i) => (
                                <React.Fragment key={i}>
                                  {p.payment_settlement === "Full"
                                    ? this.formatAmount(p.price || 0)
                                    : this.formatAmount(
                                        p.part_payment_amount || 0
                                      )}
                                  {i !== r.old.length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </td>
                            <td
                              valign="middle"
                              style={{ background: "#d4edda" }}
                            >
                              {(r.old || []).length > 0
                                ? r.old.map((p, i) => (
                                    <React.Fragment key={i}>
                                      {this.formatDate(p.date)}
                                      {i !== r.old.length - 1 && <br />}
                                    </React.Fragment>
                                  ))
                                : "-"}
                            </td>
                            <td
                              valign="middle"
                              style={{
                                background: "#d0e7ff",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                this.openSchemeModal(
                                  prevRows.flatMap(p =>
                                    (p.schemes || []).map(s => ({
                                      scheme_name: s.scheme_name,
                                      cycle_no: s.cycle_no || "-",
                                    }))
                                  )
                                )
                              }
                            >
                              {prevCount > 0 ? prevCount : "-"}
                            </td>
                            <td
                              valign="middle"
                              style={{ background: "#d0e7ff" }}
                              align="right"
                            >
                              {prevAmounts}
                            </td>
                            <td
                              valign="middle"
                              style={{ background: "#d0e7ff" }}
                            >
                              {prevDate}
                            </td>

                            <td
                              valign="middle"
                              style={{
                                background: "#f3de88",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                this.openSchemeModal(
                                  nextRows.flatMap(p =>
                                    (p.schemes || []).map(s => ({
                                      scheme_name: s.scheme_name,
                                      cycle_no: s.cycle_no || "-",
                                    }))
                                  )
                                )
                              }
                            >
                              {nextCount > 0 ? nextCount : "-"}
                            </td>
                            <td
                              valign="middle"
                              style={{ background: "#f3de88" }}
                              align="right"
                            >
                              {nextAmounts}
                            </td>
                            <td
                              valign="middle"
                              style={{ background: "#f3de88" }}
                            >
                              {nextDate}
                            </td>

                            <td
                              valign="middle"
                              style={{ background: "#b4d8ad" }}
                            >
                              {status}
                            </td>
                            <td
                              valign="middle"
                              style={{
                                background: "#e3e3e3",
                                textAlign: "center",
                              }}
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="d-flex flex-row align-items-center justify-content-center gap-2">
                                <Tooltip title="History">
                                  <Link
                                    className="fas fa-comment font-size-16"
                                    to={`/${this.state.organization_name}/PaymentHistory/${r.participant_id}`}
                                  />
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modalOpen} toggle={this.toggleModal} centered>
          <ModalHeader toggle={this.toggleModal}>Scheme Names</ModalHeader>
          <ModalBody>
            {modalSchemes.length === 0 ? (
              "No schemes found"
            ) : (
              <Table bordered responsive size="sm">
                <thead>
                  <tr style={{ textAlign: "center" }}>
                    <th>Scheme Name</th>
                    <th>Cycle No</th>
                  </tr>
                </thead>
                <tbody>
                  {modalSchemes.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ textAlign: "left" }}>{item.scheme_name}</td>
                      <td>{item.cycle_no}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

RenewalParticipants.propTypes = {
  match: PropTypes.object,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  CycleRenewalList: PropTypes.object,
  onGetCycleRenewalList: PropTypes.func,
};

const mapStateToProps = state => {
  console.log(
    "CycleRenewalList from Redux:",
    state.CycleList?.CycleRenewalList
  );
  return {
    CycleRenewalList: state.CycleList?.CycleRenewalList || [],
  };
};

const mapDispatchToProps = dispatch => ({
  onGetCycleRenewalList: id => dispatch(getcycleRenewallist(id)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(RenewalParticipants));
